import type { Allowance, AllowanceRemedy, Entitlement } from "@magicweave/api";

import { netOverageWords } from "./overage";

/**
 * Which window a plan counts credits over.
 *
 * A plan's own fact, never a studio's or an account's — two studios on the same
 * plan version read the same window. Free is `lifetime` from decisions.md's
 * published policy: one grant for the life of the account rather than a monthly
 * allowance, so every sentence that used to say "this month" has to ask first.
 */
export type AllowanceWindow = "month" | "lifetime";

/** Which window a plan counts credits over. Absent on the wire reads as `month`. */
export function windowOf(caps: { allowance_window?: AllowanceWindow | null }): AllowanceWindow {
  return caps.allowance_window === "lifetime" ? "lifetime" : "month";
}

/**
 * The window's words, so no sentence below spells "month" on its own.
 *
 * One place that knows the window, per house rules: a screen that wrote "this
 * month" of its own accord would be true for every plan today and false the
 * day Free stopped resetting.
 */
export const WINDOW_WORDS: Record<
  AllowanceWindow,
  {
    /** Suffix after "N of M credits used". */
    usedSuffix: string;
    /** Heading of the allowance panel. */
    heading: string;
    /** Unit after an included-credits figure on the plan in force. */
    perPeriod: string;
    /** Same, in a catalogue row: "5,000 credits a month". */
    catalogue: string;
    /** The sentence under the allowance bar. */
    rollover: string;
    /** "until …" clause on a refusal. */
    until: string;
    /** Name for what is gone. */
    gone: string;
  }
> = {
  month: {
    usedSuffix: " this month",
    heading: "This month",
    perPeriod: "AI credits each month",
    catalogue: "AI credits a month",
    rollover:
      "AI credits do not roll over. The allowance starts again at the beginning of each month, and anything left unused this month is gone rather than added to the next one.",
    until: " until the month starts again",
    gone: "This month's AI credits are gone",
  },
  // No Pricing v3 plan counts over a lifetime, but a private version staff
  // publish still can, so the words stay.
  lifetime: {
    usedSuffix: "",
    heading: "Your AI credits",
    perPeriod: "AI credits, given once",
    catalogue: "AI credits, once",
    rollover:
      "These AI credits are given once, not every month. What is left here is all there is until you move to a larger plan.",
    until: "",
    gone: "Your AI credits are gone",
  },
};

/**
 * Why "What was used" is not the studio's whole balance on a lifetime plan.
 *
 * `usePlanUsage` always answers for the current calendar month — the wire's
 * own docstring on `Usage.window` says so: "`lines`, `spend` and
 * `credits_used` stay calendar-period figures regardless" of which window the
 * plan counts credits over. On a monthly plan that is exactly the number the
 * allowance is compared against, and no note is owed. On a lifetime plan it
 * is a slice of a total that never resets, and a table with no note reads as
 * the studio's whole spend — so this is the one sentence that keeps "what was
 * used" from disagreeing with "what is left" above it.
 */
export function usageTableNote(window: AllowanceWindow): string | undefined {
  if (window !== "lifetime") return undefined;
  return "This calendar month only. Your plan's AI credits are given once and never reset, so this list is not your whole balance — see what is left above for that.";
}

/**
 * The five states of an allowance, in the words the top strip says them in.
 *
 * **Nothing here works out which state an account is in.** `allowance.state`
 * arrives derived on the server, and that is the whole reason it exists:
 * `decisions/0025` decision 6 puts the state, the multiple and the ceiling on
 * the entitlement "so the chip, the banner, the refusal and the email cannot
 * disagree about what 'low' means". A comparison of credits against a
 * proportion of the allowance written on this side is a second derivation of
 * `low`, and a second derivation is what the field was added to prevent — it
 * disagrees with the email the week somebody changes one of them. The plan
 * page's credit meter draws that proportion by decision of 26 September 2026,
 * which supersedes the rule for that meter alone; it still takes its colour
 * and its word from `state`, never from the proportion it draws.
 *
 * **Two of the five refuse, and their ways out are different.** The allowance
 * is gone with overage off, or the ceiling the studio set has been reached.
 * `remedy` is what separates them and it is server-derived too, because the
 * mapping from state to remedy is not the identity function: a capped studio on
 * a plan that prices no overage has a ceiling no rupee figure can lift, and its
 * way out is a bigger plan rather than a bigger number.
 */
export interface AllowanceChip {
  /**
   * Short enough for the top strip, and different per state in words rather
   * than only in tone — a chip whose two refusals are told apart by colour is
   * one refusal to anybody who cannot read the colour.
   */
  label: string;
  tone: "neutral" | "warning" | "critical";
  /** The sentence behind the label, and the way out where there is one. */
  detail: string;
}

/** One control to reach for out of a refusal, and what reaching for it does. */
export interface WayOut {
  /** The control, in the imperative. "Raise your ceiling", not "your ceiling". */
  label: string;
  /** What it does, and what it will cost. */
  detail: string;
  /**
   * The Plan page sheet this way out opens, when it has one — `packs` for
   * buying AI credits. Absent means the Plan page itself.
   */
  sheet?: "packs";
}

/**
 * What a refusal offers, read off the server's remedy and never off the state.
 *
 * `raise_plan` reaching a `capped` account is the case that makes this a
 * separate field: its plan's multiple is nought, so it has no ceiling to raise
 * and telling it to raise one would be sending somebody to a control that
 * cannot help them.
 *
 * **A list rather than a sentence, and non-empty by type.** One of the three
 * remedies is two controls, not one — `0025` decision 6 leaves two walls
 * standing and the first of them has two ways through it. A refusal that names
 * the first and stops is how somebody upgrades a plan when a ceiling would have
 * done, so the plural is in the shape of the data rather than in the prose. The
 * chip composes its one sentence out of these; the refusal on a charging control
 * renders them as the controls they are.
 */
export const WAYS_OUT: Record<AllowanceRemedy, [WayOut, ...WayOut[]]> = {
  // The three overage remedies below are reached by no Pricing v3 plan, which
  // sells no overage; a private version staff publish still can price it.
  raise_plan_or_enable_overage: [
    {
      label: "Move to a larger plan",
      detail: "More AI credits, in force straight away.",
    },
    {
      label: "Turn overage on",
      detail:
        "Set a ceiling you are happy to be billed up to, and spending carries on past the allowance at this plan’s own rate.",
    },
  ],
  raise_ceiling: [
    {
      label: "Raise your ceiling",
      detail: "You are billed for what you use past the allowance, up to the figure you set.",
    },
  ],
  raise_plan: [
    {
      label: "Move to a larger plan",
      detail: "This one prices no AI credits past its allowance, so no ceiling can lift this.",
    },
  ],
  // Pricing v3: no overage, so the way past the allowance is a pack bought
  // up front — or a larger plan, which the pack sheet's own page links to.
  buy_pack: [
    {
      label: "Buy AI credits",
      detail: "A pack of AI credits, used once this allowance is gone and good for 12 months.",
      sheet: "packs",
    },
    {
      label: "Move to a larger plan",
      detail: "More AI credits every month, in force straight away.",
    },
  ],
};

/**
 * The chip's one sentence, composed from the same list the refusal renders.
 *
 * Labels only, because this ends up in a `title` and the details belong beside
 * the control somebody is about to press rather than in a tooltip on a chip.
 * Composed rather than written out a second time: two spellings of a remedy is
 * how the chip and the refusal come to disagree about what a studio may do.
 */
function wayOut(remedy: Entitlement["allowance"]["remedy"]): string {
  if (!remedy) return "";
  const [first, ...rest] = WAYS_OUT[remedy];
  const labels = [
    first.label,
    ...rest.map((w) => w.label.charAt(0).toLowerCase() + w.label.slice(1)),
  ];
  return ` ${labels.join(", or ")}.`;
}

/**
 * The chip, or nothing at all.
 *
 * Nothing for an organisation nobody has put on a plan: it is not being metered
 * and it is not being refused anything, so a chip reading "0 credits left"
 * would tell them the one thing about their situation that is not true. The
 * plan screen draws the same distinction — see its `Current`.
 */
export function allowanceChip(entitlement: Entitlement): AllowanceChip | undefined {
  if (!entitlement.provisioned || !entitlement.plan) return undefined;

  const { allowance } = entitlement;
  const left = entitlement.credits_remaining.toLocaleString();
  const currency = entitlement.plan.currency;
  const win = windowOf(entitlement.capabilities);
  const w = WINDOW_WORDS[win];
  const used = `${entitlement.credits_used.toLocaleString()} of ${entitlement.capabilities.included_credits.toLocaleString()} AI credits used${w.usedSuffix}.`;

  switch (allowance.state) {
    case "comfortable":
      return { label: `${left} AI credits left`, tone: "neutral", detail: used };

    case "low":
      return {
        label: `Low · ${left} AI credits left`,
        tone: "warning",
        detail:
          win === "lifetime"
            ? `${used} Less than a fifth of your AI credits are left.`
            : `${used} Less than a fifth of the month's allowance is still there.`,
      };

    // Permitted and costing money, which is not the same as refused and is not
    // drawn as if it were: the studio turned this on and set the figure it is
    // spending against.
    case "over":
      return {
        label: "Past your allowance",
        tone: "warning",
        detail:
          win === "lifetime"
            ? `${w.gone} and overage is on, so ${netOverageWords(allowance.overage.accrued, currency)} has been added to your bill against the ${netOverageWords(allowance.overage.ceiling, currency)} ceiling you set.`
            : `This month's AI credits are gone and overage is on, so ${netOverageWords(allowance.overage.accrued, currency)} has been added to this month against the ${netOverageWords(allowance.overage.ceiling, currency)} ceiling you set.`,
      };

    case "spent":
      return {
        label: "No AI credits left",
        tone: "critical",
        // A plan that sells packs sells no overage, so "overage is off" would
        // name a switch this studio does not have.
        detail:
          allowance.remedy === "buy_pack"
            ? `${w.gone}, so AI work is refused${w.until}.${wayOut(allowance.remedy)}`
            : `${w.gone} and overage is off, so AI work is refused${w.until}.${wayOut(allowance.remedy)}`,
      };

    // Deliberately not the same label as `spent`. Both refuse; one is a studio
    // that has not chosen to spend past its allowance and one is a studio that
    // has, up to a figure it named, and telling them apart is the difference
    // between two remedies.
    case "capped":
      return {
        label: "Spending limit reached",
        tone: "critical",
        detail: `${
          allowance.remedy === "raise_ceiling"
            ? `You are past this month's allowance and the ${netOverageWords(allowance.overage.ceiling, currency)} ceiling you set has been reached, so AI work is refused.`
            : allowance.remedy === "buy_pack"
              ? `${w.gone}, so AI work is refused${w.until}.`
              : `${w.gone} and your plan prices none past them, so AI work is refused${w.until}.`
        }${wayOut(allowance.remedy)}`,
      };
  }
}

/**
 * The words on a control that spends credits, before it is pressed.
 *
 * **This is `0025` decision 5's other half.** The decision removes the issuance
 * allowance `0005` bundled into every plan, and the reason it gives for being
 * allowed to is that issuance stays a product decision rather than a budget one
 * "by the price being low and visible at the point of the action". If the price
 * is not on the control, the decision has no argument left — so the cost and the
 * balance-after are computed here, once, for every charging surface in the
 * console.
 *
 * **It derives no state.** Whether an action is refused is `allowance.state`,
 * and which controls help is `allowance.remedy`; both arrive derived, and this
 * function does arithmetic against the numbers beside them without recomputing
 * either. The one thing the server cannot know is the size of the action
 * somebody is looking at, which is the only reason this takes a `cost` at all.
 */
export interface PricedAction {
  /** What pressing it costs. Known before the entitlement is. */
  price: string;
  /** What is left once it has been pressed, in the unit the answer is in. */
  after: string;
  /**
   * False while the allowance has not arrived.
   *
   * The price is known without it and is shown regardless — see `after`. What is
   * not known is whether pressing would be refused, and a control that is
   * pressable in that window is a control somebody presses in the moment before
   * the wall appears under it. So this is not about the words: a surface holds
   * the control until it is true.
   */
  settled: boolean;
  /**
   * True when pressing it would be refused.
   *
   * The server's two refusing states and nothing else. A cost larger than what
   * is left is **not** this: the server refuses at the wall it derived, and a
   * console that invented a second wall would be deriving the thing decision 6
   * put on the wire to stop being derived. That case is said in `after`, in
   * words, with the control still pressable.
   */
  refused: boolean;
  /** The wall and every way through it. Absent when nothing is refused. */
  refusal?: Refusal;
}

export interface Refusal {
  /** The wall, in words. Never "not enough AI credits". */
  title: string;
  ways: [WayOut, ...WayOut[]];
}

function credits(n: number): string {
  return n === 1 ? "1 AI credit" : `${n.toLocaleString()} AI credits`;
}

/**
 * What a refused control says it ran into.
 *
 * `capped` needs the remedy to be worded at all, which is the same fork the chip
 * has: a studio at a ceiling it chose is at a figure it can raise, and a studio
 * on a plan that prices no overage is at a wall no figure can move. Naming the
 * ceiling to the second one would be describing a control it does not have.
 */
function refusalTitle(allowance: Allowance, currency: string, window: AllowanceWindow): string {
  // The month branches are written out rather than built from `WINDOW_WORDS`,
  // to keep this sentence's own apostrophe style — a typeset ’ this function
  // has always used and the chip's plainer detail strings never did.
  if (allowance.remedy === "buy_pack") {
    return window === "lifetime"
      ? WINDOW_WORDS.lifetime.gone
      : "This month’s AI credits are gone";
  }
  if (allowance.state === "spent") {
    return window === "lifetime"
      ? `${WINDOW_WORDS.lifetime.gone}, and overage is off`
      : "This month’s AI credits are gone, and overage is off";
  }
  return allowance.remedy === "raise_ceiling"
    ? `The ${netOverageWords(allowance.overage.ceiling, currency)} spending limit you set has been reached`
    : window === "lifetime"
      ? `${WINDOW_WORDS.lifetime.gone}, and this plan prices none past them`
      : "This month’s AI credits are gone, and this plan prices none past them";
}

/**
 * The cost of one action, and what the account looks like after it.
 *
 * `cost` is in credits, which is the unit every rate card entry is in — and it
 * is undefined until the server has said what the rate is. See `useMeterRate`.
 *
 * **An unknown price is not a free action, and it is not pressable.** The rate
 * comes off the active card, which staff can change at any time with no undo and
 * no per-account pinning (`decisions/0025` decision 4), so this console never
 * has a figure of its own to fall back on. A control quoting "0 credits" from a
 * read still in flight would be the exact failure the compiled-in card was
 * deleted to end, and it would be pressable.
 *
 * An entitlement that has not arrived yet still gets a price, because the price
 * does not depend on it. Only the second half waits.
 */
export function pricedAction(
  entitlement: Entitlement | undefined,
  cost: number | undefined,
): PricedAction {
  if (cost === undefined) {
    return {
      price: "checking the price",
      after: "checking what is left",
      refused: false,
      settled: false,
    };
  }

  const price = credits(cost);

  if (!entitlement) {
    return { price, after: "checking what is left", refused: false, settled: false };
  }

  // Not on a plan, so not metered and not refused anything — which is the
  // distinction `provisioned` exists to draw, and the same one the chip and the
  // plan screen's `Current` make. "0 credits left" would be the one description
  // of their situation that is false.
  if (!entitlement.provisioned || !entitlement.plan) {
    return {
      price,
      after: "nothing is metered on this organisation yet",
      refused: false,
      settled: true,
    };
  }

  const { allowance } = entitlement;
  const currency = entitlement.plan.currency;
  const win = windowOf(entitlement.capabilities);

  if (allowance.state === "spent" || allowance.state === "capped") {
    return {
      price,
      after: "nothing left to spend on it",
      refused: true,
      settled: true,
      // A refusing state with no remedy is the server breaking its own
      // contract, and the control stays refused because refusing is the side
      // that costs a studio a delay rather than a coin nobody paid for.
      ...(allowance.remedy
        ? {
            refusal: {
              title: refusalTitle(allowance, currency, win),
              ways: WAYS_OUT[allowance.remedy],
            },
          }
        : {}),
    };
  }

  const shortfall = cost - entitlement.credits_remaining;
  if (shortfall <= 0) {
    return {
      price,
      after: `${credits(entitlement.credits_remaining - cost)} left after this`,
      refused: false,
      settled: true,
    };
  }

  // Past the allowance and inside a ceiling the studio set, which is permitted
  // and costs rupees. The balance-after is therefore a rupee figure and not a
  // credit one: telling somebody they will have nought credits left would hide
  // the only number that is actually moving.
  const { overage } = allowance;
  if (overage.enabled && overage.credits_remaining >= shortfall) {
    const rupees = Math.round((shortfall * overage.rate_per_thousand_credits) / 1000);
    return {
      price,
      after: `${netOverageWords(overage.accrued + rupees, currency)} of your ${netOverageWords(overage.ceiling, currency)} ceiling after this`,
      refused: false,
      settled: true,
    };
  }

  return {
    price,
    after:
      win === "lifetime"
        ? `more than the ${credits(entitlement.credits_remaining)} you have left`
        : `more than the ${credits(entitlement.credits_remaining)} left this month`,
    refused: false,
    settled: true,
  };
}
